import React, { useCallback } from 'react';
import { Address } from './types';
import { addressesApi } from './services/addressApi';
import { SearchSection } from '../../../components/common/SearchSection';
import './AddressSearchSection.css';

interface AddressSearchSectionProps {
  selectedAddress: Address | null;
  onAddressSelect: (address: Address | null) => void;
  placeholder?: string;
  disabled?: boolean;
  label?: string;
  /** When true, exclude addresses already assigned to a property. Use false for contacts (addresses can be shared). */
  availableForProperty?: boolean;
  /** When editing a property, pass its ID so the current address can remain selected. */
  excludePropertyId?: number;
  /** Hide the remove control on the selected-address chip (row-level delete handles removal). */
  hideSelectedRemoveButton?: boolean;
}

const formatAddress = (address: Address) => {
  const { street1, street2, unit, city, state, zip } = address;
  let result = street1;
  if (street2) result += ` ${street2}`;
  if (unit) result += ` ${unit}`;
  result += `, ${city}, ${state} ${zip}`;
  return result;
};

export const AddressSearchSection: React.FC<AddressSearchSectionProps> = ({
  selectedAddress,
  onAddressSelect,
  placeholder = 'Search addresses by street, city, state, or zip...',
  disabled = false,
  label = 'Search Address',
  availableForProperty = true,
  excludePropertyId,
  hideSelectedRemoveButton = false
}) => {
  const searchFunction = useCallback(async (searchTerm: string) => {
    const response = await addressesApi.getAll({
      search: searchTerm.trim(),
      limit: 10,
      ...(availableForProperty && { availableForProperty: true, excludePropertyId })
    });
    return response.data;
  }, [availableForProperty, excludePropertyId]);

  const renderResultItem = useCallback((address: Address, onAdd: () => void, isDisabled?: boolean) => (
    <div className="search-result-item">
      <div className="search-result-item-content">
        {formatAddress(address)}
      </div>
      <div className="search-result-item-actions">
        <button
          type="button"
          className="app-btn app-btn-primary btn-small"
          onClick={onAdd}
          disabled={isDisabled}
        >
          Add
        </button>
      </div>
    </div>
  ), []);

  const renderSelectedItem = useCallback((address: Address, onRemove: () => void) => (
    <div className="search-selected-item">
      <div className="search-selected-item-content">
        📍 {formatAddress(address)}
      </div>
      {!hideSelectedRemoveButton && (
        <div className="search-selected-item-actions">
          <button
            type="button"
            className="app-btn app-btn-danger btn-small"
            onClick={onRemove}
            title="Remove address"
          >
            🗑️
          </button>
        </div>
      )}
    </div>
  ), [hideSelectedRemoveButton]);

  return (
    <SearchSection<Address>
      selectionMode="single"
      selectedItem={selectedAddress}
      onItemSelect={onAddressSelect}
      searchFunction={searchFunction}
      getByIdFunction={addressesApi.getById}
      renderResultItem={renderResultItem}
      renderSelectedItem={renderSelectedItem}
      getItemId={useCallback((address: Address) => address.id, [])}
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      entityType="address"
      emptyStateMessage="No address selected."
      selectedLabel="Selected Address"
    />
  );
};
